import { useState, useMemo } from 'react';
import { Doughnut } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend,
    CategoryScale,
    LinearScale,
} from 'chart.js';

ChartJS.register(
    ArcElement,
    Tooltip,
    Legend,
    CategoryScale,
    LinearScale
);

const StockPieChart = ({ summary }) => {
    // Colors for stock health
    const colorPalette = {
        healthyStock: '#10B981', // Green
        lowStock: '#F59E0B', // Yellow/Amber
        outOfStock: '#EF4444', // Red
    };

    // Prepare chart data
    const chartData = useMemo(() => {
        if (!summary) {
            return {
                labels: ['No Data'],
                datasets: [{
                    data: [1],
                    backgroundColor: ['#E5E7EB'],
                    borderColor: ['#D1D5DB'],
                    borderWidth: 1,
                }]
            };
        }

        const { healthyStock = 0, lowStock = 0, outOfStock = 0 } = summary;
        const total = healthyStock + lowStock + outOfStock;

        // If all values are zero, show no data
        if (total === 0) {
            return {
                labels: ['No Data'],
                datasets: [{
                    data: [1],
                    backgroundColor: ['#E5E7EB'],
                    borderColor: ['#D1D5DB'],
                    borderWidth: 1,
                }]
            };
        }

        // Filter out zero values
        const data = [];
        const labels = [];
        const colors = [];

        if (healthyStock > 0) {
            data.push(healthyStock);
            labels.push('In Stock');
            colors.push(colorPalette.healthyStock);
        }
        if (lowStock > 0) {
            data.push(lowStock);
            labels.push('Low Stock');
            colors.push(colorPalette.lowStock);
        }
        if (outOfStock > 0) {
            data.push(outOfStock);
            labels.push('Out of Stock');
            colors.push(colorPalette.outOfStock);
        }

        return {
            labels: labels,
            datasets: [
                {
                    data: data,
                    backgroundColor: colors,
                    borderColor: colors.map(color => color),
                    borderWidth: 2,
                    hoverOffset: 8,
                },
            ],
        };
    }, [summary]);

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                callbacks: {
                    label: function (context) {
                        const total = context.dataset.data.reduce((a, b) => a + b, 0);
                        const percentage = ((context.parsed / total) * 100).toFixed(1);
                        const label = context.label || '';
                        return `${label}: ${context.parsed} items (${percentage}%)`;
                    }
                }
            }
        },
        cutout: '60%',
    };

    // Calculate summary statistics
    const summaryStats = useMemo(() => {
        if (!summary) {
            return {
                totalItems: 0,
                healthyStock: 0,
                lowStock: 0,
                outOfStock: 0,
                healthyPercentage: 0,
                lowPercentage: 0,
                outPercentage: 0,
                status: 'No Data'
            };
        }

        const { healthyStock = 0, lowStock = 0, outOfStock = 0 } = summary;
        const total = healthyStock + lowStock + outOfStock;

        return {
            totalItems: total,
            healthyStock,
            lowStock,
            outOfStock,
            healthyPercentage: total > 0 ? ((healthyStock / total) * 100).toFixed(1) : 0,
            lowPercentage: total > 0 ? ((lowStock / total) * 100).toFixed(1) : 0,
            outPercentage: total > 0 ? ((outOfStock / total) * 100).toFixed(1) : 0,
            status: total === 0 ? 'No Data' :
                outOfStock > 0 ? 'Critical - Out of Stock Items' :
                    lowStock > 0 ? 'Warning - Low Stock Items' :
                        'Healthy - All Items Stocked'
        };
    }, [summary]);

    // Custom Legend
    const renderLegend = () => {
        if (!summary || summaryStats.totalItems === 0) {
            return null;
        }

        const items = [
            { label: 'In Stock', value: summaryStats.healthyStock, color: colorPalette.healthyStock },
            { label: 'Low Stock', value: summaryStats.lowStock, color: colorPalette.lowStock },
            { label: 'Out of Stock', value: summaryStats.outOfStock, color: colorPalette.outOfStock },
        ];

        return (
            <div className="flex flex-wrap justify-center gap-4 mt-2">
                {items.map((item, index) => (
                    <div key={index} className="flex items-center gap-2">
                        <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: item.color }}
                        />
                        <span className="text-xs text-gray-600">
                            {item.label}: <span className="font-semibold">{item.value}</span>
                        </span>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div className="bg-white rounded-xl border border-gray-100 px-4 py-4">
            <div className="flex justify-between items-center mb-4">
                <div>
                    <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                        Stock Health
                    </h3>
                    <p className="text-xs text-gray-400 mt-0.5">
                        {summaryStats.totalItems > 0 ?
                            `${summaryStats.totalItems} total items · ${summaryStats.healthyPercentage}% in stock` :
                            'No stock data available'}
                    </p>
                </div>

            </div>

            {/* Chart */}
            <div className="h-72">
                {summary && summaryStats.totalItems > 0 ? (
                    <Doughnut data={chartData} options={options} />
                ) : (
                    <div className="h-full flex items-center justify-center text-gray-500">
                        No stock data available
                    </div>
                )}
            </div>

            {/* Custom Legend */}
            {/* {renderLegend()} */}

            {/* Summary Stats */}
            <div className="mt-4 grid grid-cols-3 gap-3">
                <StatCard
                    label="In Stock"
                    value={summaryStats.healthyStock}
                    color="text-green-600"
                    percentage={summaryStats.healthyPercentage}
                />
                <StatCard
                    label="Low Stock"
                    value={summaryStats.lowStock}
                    color="text-yellow-600"
                    percentage={summaryStats.lowPercentage}
                />
                <StatCard
                    label="Out of Stock"
                    value={summaryStats.outOfStock}
                    color="text-red-600"
                    percentage={summaryStats.outPercentage}
                />
            </div>

        </div>
    );
};

const StatCard = ({ label, value, color, percentage }) => {
    return (
        <div className="bg-gray-50 p-3 rounded-lg hover:bg-gray-100 transition-colors">
            <p className="text-xs text-gray-500 font-medium uppercase truncate">{label}</p>
            <div className="flex items-baseline gap-2">
                <p className={`text-lg font-bold ${color}`}>
                    {value}
                </p>
                {percentage !== undefined && percentage > 0 && (
                    <span className="text-xs text-gray-400">
                        ({percentage}%)
                    </span>
                )}
            </div>
        </div>
    );
};

export default StockPieChart;